<template>
  <div class="body bank-app">
    <Navbar :show-back-button="true" @back="navigateToDashboard"/>
    
    <div class="section-header">
      <!-- <button class="back-arrow" @click="navigateToDashboard">
        →
      </button> -->
      <h3 class="section-title mt-3" style="margin-right: 10px;">{{ $t('employer.reportMande2.title') }}</h3>
    </div>
    
    <!-- Report Controls -->
    <div class="action-bar">
      <div style="display: inline-flex; gap: 2px;">
        <button 
          class="refresh-btn" 
          @click="refreshCurrentTab"
          :disabled="loading"
          :title="$t('employer.reportMande2.reload_title')"
        >
          🔄
          <span class="refresh-text">{{ $t('employer.reportMande2.reload') }}</span>
        </button>
  
        <!-- Print Button -->
        <button class="print-btn" @click="printReport">
          🖨️ 
          <span class="print-text">{{ $t('employer.reportMande2.print') }}</span>
        </button>
  
        <!-- Export Button -->
        <button class="export-btn" @click="exportToExcel">
          📊
          <span class="export-text">{{ $t('employer.reportMande2.excel') }}</span>
        </button>
      </div>

      <!-- Report Type Selector -->
      <div class="currency-selector">
        <label for="report-type-select" class="currency-label">
          {{ $t('employer.reportMande2.report_type') }}
        </label>
        <select 
          id="report-type-select"
          v-model="selectedReportType" 
          @change="loadReportData"
          class="currency-select"
          :disabled="loading"
        >
          <option value="0">{{ $t('employer.reportMande2.type_all') }}</option>
          <option value="1">{{ $t('employer.reportMande2.type_debitors') }}</option>
          <option value="2">{{ $t('employer.reportMande2.type_creditors') }}</option>
        </select>
      </div>

      <!-- Search Input -->
      <div class="search-container">
        <input 
          type="text" 
          v-model="searchQuery"
          :placeholder="$t('employer.reportMande2.search_placeholder')"
          class="search-input"
          @input="onSearch"
        />
        <span class="search-icon">🔍</span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <span>{{ $t('employer.reportMande2.loading') }}</span>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-state">
      <div class="error-icon">⚠️</div>
      <h4>{{ $t('employer.reportMande2.error_title') }}</h4>
      <p>{{ error }}</p>
      <button class="retry-btn" @click="loadReportData">
        {{ $t('employer.reportMande2.retry') }}
      </button>
    </div>

    <!-- Report Content -->
    <div v-if="!loading && !error" class="report-content">
      <div class="report-header">
        <h4 style="color: var(--text);">{{ $t(getActiveTabTitle) }}</h4>
        <div class="report-info">
          <span>
            {{ $t('employer.reportMande2.customer_count') }} {{ filteredCustomers.length }}
          </span>
          <span> | </span>
          <span>
            {{ $t('employer.reportMande2.currency_count') }} {{ totalCurrencies }}
          </span>
          <span> | </span>
          <span>
            {{ $t('employer.reportMande2.total_balance') }} {{ formatCurrency(currentTabTotalBalance) }}
          </span>
        </div>
        
        <!-- Sorting Controls -->
        <div class="sort-controls">
          <select v-model="sortField" @change="sortOrder = 'desc'" class="sort-select">
            <option value="total_mande">{{ $t('employer.reportMande2.sort_total') }}</option>
            <option value="Title">{{ $t('employer.reportMande2.sort_name') }}</option>
            <option value="Ei5">{{ $t('employer.reportMande2.sort_code') }}</option>
            <option value="total_SumBed">{{ $t('employer.reportMande2.sort_debit') }}</option>
            <option value="total_SumBes">{{ $t('employer.reportMande2.sort_credit') }}</option>
          </select>
          <button @click="sortOrder = sortOrder === 'asc' ? 'desc' : 'asc'" class="sort-order-btn">
            {{ sortOrder === 'asc' 
                ? $t('employer.reportMande2.sort_asc')
                : $t('employer.reportMande2.sort_desc') }}
          </button>
        </div>
      </div>

      <div v-if="filteredCustomers.length === 0" class="empty-state">
        <span>{{ $t('employer.reportMande2.empty') }}</span>
      </div>

      <div v-else class="customer-cards-container">
        <div class="customer-cards-grid">
          <div v-for="customer in paginatedCustomers" :key="customer.Ei5" :data-customer-id="customer.Ei5" class="customer-card">
            <div class="card-body">

              <!-- Customer Header -->
              <div class="row customer-header-row">
                <div class="customer-title-section">
                  <div class="duration-section">

                    <span class="duration-label">{{ $t('employer.reportMande2.name') }}</span>
                    <span class="duration-value">{{ customer.Title || $t('employer.reportMande2.not_set') }}</span>
                    
                    <span class="duration-label" style="margin-right: auto;">{{ $t('employer.reportMande2.phone') }}</span>
                    <span class="duration-value">{{ customer.Tel2 || $t('employer.reportMande2.not_set') }}</span>

                  </div>
                </div>
              </div>

              <!-- Currency Cards -->
              <div class="currency-cards-grid">
                <div v-for="currency in customer.currencies" :key="currency.arz" class="currency-card">
                  <div class="currency-card-body">

                    <div class="currency-section" v-if="currency.SumBed!=0 || currency.SumBes!=0">
                      <div class="section-label">{{ $t('employer.reportMande2.rial') }}</div>
                      <div class="section-content">

                        <div class="currency-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.debit') }}</span>
                          <span class="currency-value bed-amount">
                            {{ formatCurrency(currency.SumBed) }}
                          </span>
                        </div>

                        <div class="currency-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.credit') }}</span>
                          <span class="currency-value bes-amount">
                            {{ formatCurrency(currency.SumBes) }}
                          </span>
                        </div>

                        <div class="currency-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.balance') }}</span>
                          <span class="currency-value" :class="getBalanceClass(currency.mande)">
                            {{ formatCurrency(currency.mande) }}
                          </span>
                          <div class="currency-status" :class="getCurrencyStatusClass(currency)">
                            {{ getCurrencyStatus(currency) }}
                          </div>
                        </div>

                      </div>
                    </div>

                    <!-- Foreign Currencies -->
                    <div v-if="currency.arz !== 'ریال' && currency.SumBed_A != 0" class="currency-section foreign-section">
                      <div class="section-label">{{ currency.arz }}:</div>

                      <div class="section-content">
                        <div class="currency-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.debit') }}</span>
                          <span class="currency-value bed-amount">{{ formatCurrency(currency.SumBed_A) }}</span>
                        </div>

                        <div class="currency-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.credit') }}</span>
                          <span class="currency-value bes-amount">{{ formatCurrency(currency.SumBes_A) }}</span>
                        </div>

                        <div class="currency-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.balance') }}</span>
                          <span class="currency-value" :class="getBalanceClass(currency.mande_A)">
                            {{ formatCurrency(currency.mande_A) }}
                          </span>
                        </div>

                        <div v-if="currency.Cur_rate && currency.Cur_rate > 0" class="currency-row rate-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.today_rate') }}</span>
                          <span class="currency-value rate-value">{{ formatCurrency(currency.Cur_rate) }}</span>
                        </div>

                        <div v-if="currency.Cur_mandeR_A" class="currency-row value-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.today_value_rial') }}</span>
                          <span class="currency-value value-amount">{{ formatCurrency(currency.Cur_mandeR_A) }}</span>
                        </div>

                        <div v-if="currency.mandeR_A" class="currency-row rial-balance-row">
                          <span class="currency-label">{{ $t('employer.reportMande2.record_value_rial') }}</span>
                          <span class="currency-value rial-balance">{{ formatCurrency(currency.mandeR_A) }}</span>
                        </div>

                        <div v-if="currency.mandeR_A" class="currency-row rial-balance-row" style="background:#ecebffaf;">
                          <span class="currency-label">{{ $t('employer.reportMande2.tasir_today') }}</span>
                          <span class="currency-value rial-balance">
                            {{ formatCurrency(Math.abs(currency.Cur_mandeR_A - currency.mandeR_A)) }}
                            <span style="color:red;" v-if="currency.Cur_mandeR_A-currency.mandeR_A>0">
                              {{ $t('employer.reportMande2.loss') }}
                            </span>
                            <span style="color:green;" v-else>
                              {{ $t('employer.reportMande2.profit') }}
                            </span>
                          </span>
                        </div>

                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <button 
                class="details-btn"
                @click="selectCustomer(customer.Ei5)"
                :disabled="loading"
              >
                {{ $t('employer.reportMande2.details') }}
              </button>

            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="filteredCustomers.length > itemsPerPage" class="pagination-controls">
        <button 
          @click="prevPage" 
          :disabled="currentPage === 1"
          class="pagination-btn"
        >
          {{ $t('employer.reportMande2.prev') }}
        </button>

        <span class="page-info">
          {{ $t('employer.reportMande2.page') }}
          {{ currentPage }}
          {{ $t('employer.reportMande2.of') }}
          {{ totalPages }}
        </span>

        <button 
          @click="nextPage" 
          :disabled="currentPage === totalPages"
          class="pagination-btn"
        >
          {{ $t('employer.reportMande2.next') }}
        </button>
      </div>

    </div>
  </div>
</template>


<script>
import { computed, onMounted, ref, watch, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { employerService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'


export default {
  name: 'ReportMande2',
  components: {
    Navbar
  },
  setup() {
    const router = useRouter()
    
    // Data states
    const loading = ref(false)
    const error = ref(null)
    const searchQuery = ref('')
    const activeCustomerTab = ref('all') // 'all', 'bed', 'bes', 'mixed'
    
    // Report data
    const reportData = ref({
      data: [],
      total_records: 0,
      total_customers: 0,
      total_debit: 0,
      total_credit: 0,
      total_balance: 0
    })
    
    // Report type
    const selectedReportType = ref(0) // 0 = all, 1 = bed, 2 = bes
    
    // Pagination states
    const itemsPerPage = 5 // Less items because each has multiple currencies
    const currentPage = ref(1)
    
    // Sorting states
    const sortField = ref('total_mande')
    const sortOrder = ref('desc')
    
    // Customer tabs
    const customerTabs = computed(() => {
      const customers = reportData.value.data || []
      const counts = {
        all: customers.length,
        bed: customers.filter(c => c.overall_m === 'بدهکار').length,
        bes: customers.filter(c => c.overall_m === 'بستانکار').length,
        mixed: customers.filter(c => c.overall_m === 'مختلط').length
      }
      
      return [
        { key: 'all', label: 'همه مشتریان', count: counts.all },
        { key: 'bed', label: 'بدهکاران', count: counts.bed },
        { key: 'bes', label: 'بستانکاران', count: counts.bes },
        { key: 'mixed', label: 'مختلط', count: counts.mixed }
      ]
    })
    
    // Load report data
    const loadReportData = async () => {
      loading.value = true
      error.value = null
      
      try {
        console.log(`📊 Loading report 2 with type: ${selectedReportType.value}`)
        
        const request = {
          bedorBes: parseInt(selectedReportType.value) || 0
        }
        
        const response = await employerService.getMandeReport2(request)
        
        if (response.data.success) {
          reportData.value = response.data
          console.log(`✅ Report 2 loaded: ${response.data.total_customers} customers, ${response.data.total_records} currency records`)
          
          // Reset to first page and all tab
          currentPage.value = 1
          activeCustomerTab.value = 'all'

          setTimeout(() => {
            checkForReturnNavigation()
          }, 100)
        } else {
          throw new Error(response.data.message || 'خطا در بارگذاری گزارش')
        }
      } catch (err) {
        console.error('❌ Error loading mande report 2:', err)
        error.value = err.response?.data?.detail || err.message || 'خطا در بارگذاری گزارش'
      } finally {
        loading.value = false
      }
    }
    
    // Filter customers based on active tab
    const filteredCustomers = computed(() => {
      let customers = reportData.value.data || []
      
      // Filter by customer type
      if (activeCustomerTab.value !== 'all') {
        customers = customers.filter(customer => {
          switch (activeCustomerTab.value) {
            case 'bed':
              return customer.overall_m === 'بدهکار'
            case 'bes':
              return customer.overall_m === 'بستانکار'
            case 'mixed':
              return customer.overall_m === 'مختلط'
            default:
              return true
          }
        })
      }
      
      // Apply search
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        customers = customers.filter(customer => 
          customer.Title?.toLowerCase().includes(query) ||
          customer.Ei5?.toString().includes(query) ||
          customer.Tel2?.includes(query)
        )
      }
      
      // Apply sorting
      return sortData(customers)
    })
    
    // Sorting function
    const sortData = (data) => {
      return [...data].sort((a, b) => {
        let aValue, bValue
        
        switch (sortField.value) {
          case 'Ei5':
            aValue = a.Ei5
            bValue = b.Ei5
            break
          case 'Title':
            aValue = a.Title || ''
            bValue = b.Title || ''
            break
          case 'total_SumBed':
            aValue = a.total_SumBed || 0
            bValue = b.total_SumBed || 0
            break
          case 'total_SumBes':
            aValue = a.total_SumBes || 0
            bValue = b.total_SumBes || 0
            break
          case 'total_mande':
            aValue = a.total_mande || 0
            bValue = b.total_mande || 0
            break
          default:
            aValue = a.total_mande || 0
            bValue = b.total_mande || 0
        }
        
        if (sortOrder.value === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
        }
      })
    }
    
    // Pagination
    const paginatedCustomers = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage
      return filteredCustomers.value.slice(start, start + itemsPerPage)
    })
    
    const totalPages = computed(() => {
      return Math.ceil(filteredCustomers.value.length / itemsPerPage)
    })
    
    const prevPage = () => currentPage.value > 1 && currentPage.value--
    const nextPage = () => currentPage.value < totalPages.value && currentPage.value++
    
    // Search handler
    const onSearch = () => {
      currentPage.value = 1
    }
    
    // Tab functions
    const setActiveCustomerTab = (tab) => {
      activeCustomerTab.value = tab
      currentPage.value = 1
    }
    
    const getActiveTabTitle = computed(() => {
      switch (activeCustomerTab.value) {
        case 'all': return 'employer.reportMande2.all_customer_list'
        case 'bed': return 'لیست مشتریان بدهکار'
        case 'bes': return 'لیست مشتریان بستانکار'
        case 'mixed': return 'لیست مشتریان مختلط'
        default: return 'لیست مشتریان'
      }
    })
    
    // Helper functions
    const getCurrencySymbol = (currencyCode) => {
      const symbols = {
        'ریال': '﷼',
        'دلار': '$',
        'یورو': '€',
        'درهم': 'د.إ',
        'پوند': '£',
        'یوان': '¥',
        'لیر': '₺'
      }
      return symbols[currencyCode] || currencyCode
    }
    
    const getBalanceClass = (balance) => {
      if (balance > 0) return 'bed-amount'
      if (balance < 0) return 'bes-amount'
      return ''
    }
    
    const getCustomerTypeClass = (customer) => {
      switch (customer.overall_m) {
        case 'بدهکار': return 'customer-type-bed'
        case 'بستانکار': return 'customer-type-bes'
        case 'مختلط': return 'customer-type-mixed'
        default: return 'customer-type-none'
      }
    }
    
    const getCurrencyStatus = (currency) => {
      if (currency.m && currency.m.trim()) return currency.m
      if (currency.m_A && currency.m_A.trim()) return currency.m_A
      return 'بدون مانده'
    }
    
    const getCurrencyStatusClass = (currency) => {
      const status = getCurrencyStatus(currency)
      if (status === 'بدهکار') return 'status-bed'
      if (status === 'بستانکار') return 'status-bes'
      return 'status-none'
    }
    
    // Totals
    const totalCurrencies = computed(() => {
      return filteredCustomers.value.reduce((total, customer) => 
        total + (customer.currencies?.length || 0), 0
      )
    })
    
    const currentTabTotalBalance = computed(() => {
      return filteredCustomers.value.reduce((total, customer) => 
        total + (customer.total_mande || 0), 0
      )
    })
    
    // Balance status
    const getTotalBalanceClass = computed(() => {
      const balance = reportData.value.total_balance || 0
      if (balance > 0) return 'bed-amount'
      if (balance < 0) return 'bes-amount'
      return ''
    })
    
    const getBalanceStatusClass = computed(() => {
      const balance = reportData.value.total_balance || 0
      if (balance > 0) return 'balance-status-bed'
      if (balance < 0) return 'balance-status-bes'
      return 'balance-status-zero'
    })
    
    const getBalanceStatusText = computed(() => {
      const balance = reportData.value.total_balance || 0
      if (balance > 0) return 'بدهکار'
      if (balance < 0) return 'بستانکار'
      return 'متوازن'
    })
    
    // Navigation
    const navigateToDashboard = () => {
      cleanupReturnData()
      router.push('/employer/dashboard')
    }
    
    // Export functions
    const printReport = () => {
      console.log('Printing report 2...')
      window.print()
    }
    
    const exportToExcel = () => {
      console.log('Exporting report 2 to Excel...')
      // Implement Excel export here
    }
    
    // Format functions
    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR').format(amount)
    }
    
    // Refresh current tab
    const refreshCurrentTab = async () => {
      await loadReportData(true)
    }

    const selectCustomer = (subcode) => {
      console.log('✅ Selected customer:', subcode)
      localStorage.setItem('selectedCustomer', subcode)
      localStorage.setItem('fromwhere', 'reportMande2')
      
      // Save the clicked customer for return navigation
      localStorage.setItem('fromwhichcustomer', subcode)
      // Also save current page and tab for exact positioning
      localStorage.setItem('returnPage', currentPage.value.toString())
      localStorage.setItem('returnTab', activeCustomerTab.value)
      localStorage.setItem('returnSearch', searchQuery.value)
      localStorage.setItem('returnReportType', selectedReportType.value.toString())

      router.push('/employer/customer-factors')
    }

    // Add this after your other functions
    const checkForReturnNavigation = () => {
      const fromWhichCustomer = localStorage.getItem('fromwhichcustomer')
      const returnPage = localStorage.getItem('returnPage')
      const returnTab = localStorage.getItem('returnTab')
      const returnSearch = localStorage.getItem('returnSearch')
      const returnReportType = localStorage.getItem('returnReportType')
      
      if (fromWhichCustomer) {
        console.log('🔄 Returning to previously clicked customer:', fromWhichCustomer)
        
        // Restore the previous state
        if (returnPage) currentPage.value = parseInt(returnPage)
        if (returnTab) activeCustomerTab.value = returnTab
        if (returnSearch) searchQuery.value = returnSearch
        if (returnReportType) selectedReportType.value = parseInt(returnReportType)
        
        // Clear the return data after restoring
        localStorage.removeItem('fromwhichcustomer')
        localStorage.removeItem('returnPage')
        localStorage.removeItem('returnTab')
        localStorage.removeItem('returnSearch')
        localStorage.removeItem('returnReportType')
        
        // After data loads, scroll to the customer
        setTimeout(() => {
          scrollToCustomer(fromWhichCustomer)
        }, 500) // Small delay to ensure data is rendered
      }
    }

    // Function to scroll to a specific customer
    const scrollToCustomer = (customerId) => {
      setTimeout(() => {
        const customerElement = document.querySelector(`[data-customer-id="${customerId}"]`)
        if (customerElement) {
          customerElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          })
          
          // Optional: Highlight the customer
          customerElement.classList.add('highlight-customer')
          setTimeout(() => {
            customerElement.classList.remove('highlight-customer')
          }, 3000)
        }
      }, 100)
    }

    // Add cleanup function
    const cleanupReturnData = () => {
      // Only clear if we're not coming from customer-factors
      const fromWhere = localStorage.getItem('fromwhere')
      if (fromWhere !== 'customer-factors') {
        localStorage.removeItem('fromwhichcustomer')
        localStorage.removeItem('returnPage')
        localStorage.removeItem('returnTab')
        localStorage.removeItem('returnSearch')
        localStorage.removeItem('returnReportType')
      }
    }

    // Add to onBeforeUnmount
    onBeforeUnmount(() => {
      //cleanupReturnData()
    })
    
    // Watch for report type changes
    watch(selectedReportType, () => {
      loadReportData()
    })
    
    // Initialize
    onMounted(async () => {
      await loadReportData()
      
      // Check if we have return data before loading (for initial state)
      const fromWhichCustomer = localStorage.getItem('fromwhichcustomer')
      if (fromWhichCustomer) {
        const returnPage = localStorage.getItem('returnPage')
        const returnTab = localStorage.getItem('returnTab')
        const returnSearch = localStorage.getItem('returnSearch')
        const returnReportType = localStorage.getItem('returnReportType')
        
        if (returnPage) currentPage.value = parseInt(returnPage)
        if (returnTab) activeCustomerTab.value = returnTab
        if (returnSearch) searchQuery.value = returnSearch
        if (returnReportType) selectedReportType.value = parseInt(returnReportType)
      }
    })
    
    return {
      // Data
      loading,
      error,
      searchQuery,
      activeCustomerTab,
      customerTabs,
      reportData,
      
      // UI states
      selectedReportType,
      
      // Pagination
      currentPage,
      paginatedCustomers,
      totalPages,
      itemsPerPage,
      
      // Sorting
      sortField,
      sortOrder,
      
      // Computed
      filteredCustomers,
      totalCurrencies,
      currentTabTotalBalance,
      getActiveTabTitle,
      getTotalBalanceClass,
      getBalanceStatusClass,
      getBalanceStatusText,
      
      // Methods
      selectCustomer,
      loadReportData,
      onSearch,
      setActiveCustomerTab,
      prevPage,
      nextPage,
      refreshCurrentTab,
      printReport,
      exportToExcel,
      navigateToDashboard,
      formatCurrency,
      getCurrencySymbol,
      getBalanceClass,
      getCustomerTypeClass,
      getCurrencyStatus,
      getCurrencyStatusClass,

      checkForReturnNavigation,
      scrollToCustomer,
      cleanupReturnData,
    }
  }
}
</script>

<style scoped>
.bank-app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  direction: rtl;
  padding: 10px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #667eea;
}

.section-title {
  font-size: 1.4rem;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 5px 15px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  color: #667eea;
  margin-left: 15px;
}

.back-arrow:hover {
  background-color: #f0f0f0;
}

/* Action Bar */
.action-bar {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
  padding: 15px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  flex-wrap: wrap;
}

.refresh-btn, .print-btn, .export-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.refresh-btn {
  background: #3498db;
  color: white;
}

.refresh-btn:hover:not(:disabled) {
  background: #2980b9;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.print-btn {
  background: #2c3e50;
  color: white;
}

.print-btn:hover {
  background: #34495e;
}

.export-btn {
  background: #27ae60;
  color: white;
}

.export-btn:hover {
  background: #219653;
}

/* Currency Selector */
.currency-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
}

.currency-label {
  font-weight: 500;
  color: #555;
}

.currency-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  min-width: 150px;
}

/* Search Container */
.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  direction: rtl;
  transition: border-color 0.3s;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-top: 4px solid;
}

.bed-summary {
  border-top-color: #e74c3c;
}

.bes-summary {
  border-top-color: #27ae60;
}

.total-summary {
  border-top-color: #3498db;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.summary-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.summary-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

.bed-badge {
  background: #e74c3c;
}

.bes-badge {
  background: #27ae60;
}

.total-badge {
  background: #3498db;
}

.summary-amount {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 10px;
}

.bed-summary .summary-amount {
  color: #e74c3c;
}

.bes-summary .summary-amount {
  color: #27ae60;
}

.summary-count, .summary-status {
  font-size: 14px;
  color: #666;
}

.balance-status-bed {
  color: #e74c3c;
  font-weight: 500;
}

.balance-status-bes {
  color: #27ae60;
  font-weight: 500;
}

.balance-status-zero {
  color: #666;
  font-weight: 500;
}

/* Customer Tabs */
.customer-tabs {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 25px;
}

.tab-buttons {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
}

.tab-button {
  flex: 1;
  padding: 15px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  background-color: #e9ecef;
  color: #333;
}

.tab-button.active {
  color: #667eea;
  border-bottom-color: #667eea;
  background-color: white;
}

.tab-count {
  font-size: 13px;
  opacity: 0.8;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: #e74c3c;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.error-state h4 {
  margin: 0 0 10px 0;
  color: #333;
}

.error-state p {
  color: #666;
  margin-bottom: 20px;
}

.retry-btn {
  padding: 10px 25px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.retry-btn:hover {
  background: #2980b9;
}

/* Report Content */
.report-content {
  
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
  gap: 15px;
}

.report-header h4 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.report-info {
  color: #666;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8f9fa;
  padding: 8px 15px;
  border-radius: 8px;
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
}

.sort-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  min-width: 140px;
  direction: rtl;
}

.sort-order-btn {
  padding: 8px 15px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.sort-order-btn:hover {
  background: #2980b9;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 16px;
  background: white;
  border-radius: 12px;
  border: 2px dashed #ddd;
  margin: 20px 0;
}

/* Customer Cards Container */
.customer-cards-container {
  padding: 10px 0;
}

.customer-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

@media (max-width: 768px) {
  .customer-cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .customer-cards-grid {
    grid-template-columns: 1fr;
  }
}

/* Customer Card */
.customer-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 12px var(--shadow-1);
  border: 1px solid var(--card-border);
  transition: all 0.3s ease;
  overflow: hidden;
  height: 100%;
}

.customer-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px var(--shadow-2);
  border-color: var(--primary);
}

/* Card Body */
.card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Customer Header Row */
.customer-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--card-border);
}

.customer-title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.customer-id {
  display: flex;
  align-items: center;
  gap: 8px;
}

.id-label {
  font-size: 13px;
  color: var(--subtext);
  font-weight: 500;
}

.id-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--heading);
  background: var(--bg);
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid var(--card-border);
}

.customer-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--heading);
  line-height: 1.4;
}

.customer-type-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.customer-type-bed {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(249, 115, 22, 0.1) 100%);
  color: var(--orange-1);
  border: 1px solid rgba(249, 115, 22, 0.2);
}

.customer-type-bes {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
  color: var(--green);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.customer-type-mixed {
  background: linear-gradient(135deg, rgba(100, 116, 139, 0.2) 0%, rgba(100, 116, 139, 0.1) 100%);
  color: var(--muted);
  border: 1px solid rgba(100, 116, 139, 0.2);
}

.customer-type-none {
  background: var(--bg);
  color: var(--subtext);
  border: 1px solid var(--card-border);
}

/* Customer Summary Row */
.customer-summary-row {
  background: var(--bg);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid var(--card-border);
}

.summary-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
}

.summary-label {
  font-size: 14px;
  color: var(--subtext);
  font-weight: 500;
}

.summary-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
}

.bed-amount {
  color: var(--orange-1);
}

.bes-amount {
  color: var(--green);
}

/* Currency Header Row */
.currency-header-row {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.currency-breakdown-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: var(--heading);
  background: var(--bg);
  padding: 10px 20px;
  border-radius: 20px;
  border: 1px solid var(--card-border);
}

.currency-count {
  font-size: 13px;
  color: var(--muted);
  font-weight: normal;
}

/* Currency Cards Grid */
.currency-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
  margin-top: 10px;
}

.currency-card {
  background: var(--card-bg);
  border-radius: 10px;
  border: 1px solid var(--card-border);
  overflow: hidden;
  transition: all 0.3s ease;
}

.currency-card:hover {
  border-color: var(--primary);
  box-shadow: 0 4px 12px var(--shadow-1);
}

.currency-card-body {
  padding: 15px;
}

/* Currency Header */
.currency-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--card-border);
}

.currency-name {
  display: flex;
  align-items: center;
  gap: 10px;
}

.currency-symbol {
  font-size: 20px;
  font-weight: bold;
  color: var(--heading);
}

.currency-code {
  font-size: 16px;
  font-weight: 600;
  color: var(--heading);
}

.currency-status {
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
}

.status-bed {
  background: rgba(249, 115, 22, 0.15);
  color: var(--orange-1);
}

.status-bes {
  background: rgba(16, 185, 129, 0.15);
  color: var(--green);
}

.status-none {
  background: var(--bg);
  color: var(--muted);
}

/* Currency Section */
.currency-section {
  margin-bottom: 15px;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--heading);
  margin-bottom: 8px;
  padding-right: 5px;
}

.section-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.currency-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.currency-label {
  font-size: 13px;
  color: var(--muted);
  font-weight: 500;
}

.currency-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

/* Foreign Currency Section */
.foreign-section {
  background: var(--bg);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid var(--card-border);
}

.foreign-section .section-label {
  color: var(--heading);
}

/* Special Rows */
.rate-row {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--card-border);
}

.rate-value {
  color: var(--orange-1);
}

.value-row {
  background: rgba(16, 185, 129, 0.1);
  padding: 8px;
  border-radius: 6px;
  margin-top: 5px;
}

.value-amount {
  color: var(--green);
  font-weight: 700;
}

.rial-balance-row {
  background: rgba(59, 130, 246, 0.1);
  padding: 8px;
  border-radius: 6px;
  margin-top: 5px;
}

.rial-balance {
  color: var(--primary);
  font-weight: 700;
}

/* Additional Responsive Styles */
@media (max-width: 768px) {
  .customer-header-row {
    flex-direction: column;
    gap: 12px;
  }
  
  .customer-type-badge {
    align-self: flex-start;
  }
  
  .summary-section {
    grid-template-columns: 1fr;
  }
  
  .summary-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .currency-cards-grid {
    grid-template-columns: 1fr;
  }
  
  .currency-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .card-body {
    padding: 15px;
  }
  
  .currency-breakdown-title {
    font-size: 14px;
    padding: 8px 16px;
  }
  
  .currency-card-body {
    padding: 12px;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-container {
    width: 100%;
  }
  
  .summary-cards {
    grid-template-columns: 1fr;
  }
  
  .customer-cards-grid {
    grid-template-columns: 1fr;
  }
  
  .currency-cards-grid {
    grid-template-columns: 1fr;
  }
  
  .summary-section {
    grid-template-columns: 1fr;
  }
  
  .report-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .sort-controls {
    width: 100%;
    justify-content: space-between;
  }
  
  .sort-select {
    flex-grow: 1;
  }
  
  .tab-buttons {
    flex-wrap: wrap;
  }
  
  .tab-button {
    flex: 1 0 calc(50% - 10px);
    margin: 5px;
  }
}

@media (max-width: 480px) {
  .customer-header-row {
    flex-direction: column;
    gap: 10px;
  }
  
  .customer-type-badge {
    align-self: flex-start;
  }
  
  .currency-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
}

@media print {
  .action-bar,
  .summary-cards,
  .customer-tabs,
  .sort-controls,
  .pagination-controls {
    display: none !important;
  }
  
  .bank-app {
    padding: 0;
    background: white;
  }
  
  .customer-card {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  
  .currency-card {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}

.duration-section {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.duration-item {
  display: inline;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 5px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.duration-label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
}

.duration-value {
  font-size: 14px;
  font-weight: 700;
  color: #495057;
}

/* Details Button */
.details-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 10px;
  transition: all 0.3s ease;
  width: 100%;
}

.details-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.details-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}
/* Add to your CSS */
.highlight-customer {
  animation: highlight-pulse 3s ease-in-out;
  border: 2px solid #4a6baf;
  box-shadow: 0 0 20px rgba(74, 107, 175, 0.5);
}

@keyframes highlight-pulse {
  0%, 100% {
    box-shadow: 0 0 20px rgba(74, 107, 175, 0.5);
    border-color: #4a6baf;
  }
  50% {
    box-shadow: 0 0 30px rgba(74, 107, 175, 0.8);
    border-color: #2c4a8c;
  }
}

/* -------------------------------------------------------
   Global Layout
------------------------------------------------------- */
.bank-app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  direction: rtl;
  padding: 10px;
}

/* -------------------------------------------------------
   SECTION HEADER
------------------------------------------------------- */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #667eea;
}

.section-title {
  font-size: 1.4rem;
  color: #2d3748;
  font-weight: 600;
  margin: 0;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 6px 14px;
  border-radius: 8px;
  color: #667eea;
  margin-left: 15px;
  transition: 0.25s ease;
}
.back-arrow:hover {
  background: #f1f3f9;
}

/* -------------------------------------------------------
   ACTION BAR
------------------------------------------------------- */
.action-bar {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  background: var(--card-bg);
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0px 3px 10px rgba(0,0,0,0.06);
  margin-bottom: 30px;
}

/* Buttons */
.refresh-btn,
.print-btn,
.export-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: 0.25s ease;
}

.refresh-btn {
  background: #3498db;
}
.refresh-btn:hover:not(:disabled) {
  background: #2980b9;
}
.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.print-btn {
  background: #2c3e50;
}
.print-btn:hover {
  background: #34495e;
}

.export-btn {
  background: #27ae60;
}
.export-btn:hover {
  background: #219653;
}

/* -------------------------------------------------------
   SELECT FIELD (Report Type)
------------------------------------------------------- */
.currency-selector {
  display: flex;
  align-items: center;
  gap: 10px;
}

.currency-label {
  font-weight: 500;
  color: #4a5568;
}

.currency-select {
  padding: 8px 12px;
  border: 1px solid #d6d9e0;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  transition: 0.25s ease;
}
.currency-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.25);
}

/* -------------------------------------------------------
   SEARCH
------------------------------------------------------- */
.search-container {
  position: relative;
  min-width: 260px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #d6d9e0;
  border-radius: 8px;
  font-size: 14px;
  transition: 0.25s ease;
}
.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.25);
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}

/* -------------------------------------------------------
   LOADING STATE
------------------------------------------------------- */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.loading-spinner {
  width: 42px;
  height: 42px;
  border: 3px solid #e5e7eb;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* -------------------------------------------------------
   ERROR STATE
------------------------------------------------------- */
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: #e74c3c;
}

.retry-btn {
  background: #3498db;
  color: white;
  padding: 10px 22px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.25s ease;
}
.retry-btn:hover {
  background: #2980b9;
}

/* -------------------------------------------------------
   REPORT HEADER
------------------------------------------------------- */
.report-header {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 15px;
}

.report-info {
  font-size: 14px;
  color: #4a5568;
  display: flex;
  gap: 10px;
  background: var(--card-bg);
  color: var(--text);
  padding: 8px 15px;
  border-radius: 8px;
}

/* -------------------------------------------------------
   SORT CONTROLS
------------------------------------------------------- */
.sort-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sort-select {
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #d6d9e0;
  background: white;
  font-size: 14px;
}

.sort-order-btn {
  background: #667eea;
  border: none;
  color: white;
  padding: 8px 15px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: 0.25s;
}

.sort-order-btn:hover {
  background: #5a6ddb;
}

/* -------------------------------------------------------
   EMPTY STATE
------------------------------------------------------- */
.empty-state {
  padding: 60px 20px;
  border: 2px dashed #d6d9e0;
  background: var(--card-bg);
  text-align: center;
  border-radius: 12px;
  color: #6b7280;
}

/* -------------------------------------------------------
   CUSTOMER CARDS
------------------------------------------------------- */
.customer-cards-grid {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
}

.customer-card {
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 3px 10px rgba(0,0,0,0.06);
  transition: 0.25s ease;
}
.customer-card:hover {
  transform: translateY(-3px);
  border-color: #667eea;
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.2);
}

/* -------------------------------------------------------
   BUTTON: DETAILS
------------------------------------------------------- */
.details-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 10px 14px;
  width: 100%;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.25s ease;
}
.details-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(102, 126, 234, 0.35);
}
.details-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* -------------------------------------------------------
   HIGHLIGHT PULSE
------------------------------------------------------- */
.highlight-customer {
  animation: highlight-pulse 3s ease-in-out;
  border: 2px solid #4a6baf;
  box-shadow: 0 0 20px rgba(74, 107, 175, 0.5);
}

@keyframes highlight-pulse {
  0%, 100% {
    box-shadow: 0 0 20px rgba(74, 107, 175, 0.5);
    border-color: #4a6baf;
  }
  50% {
    box-shadow: 0 0 30px rgba(74, 107, 175, 0.8);
    border-color: #2c4a8c;
  }
}
</style>